import { trigger } from '@angular/animations';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  inject,
} from '@angular/core';
import { GalleryDto } from '@nusign/picflow-backend-types';
import { Observable, Subject, delay, filter, map, take, takeUntil } from 'rxjs';
import { moveRightIf } from '../../../animations/move-right.if.animation';
import { clamp } from '../../../lib/clamp';
import { debounce } from '../../../lib/debounce';
import { AbsPipe } from '../../../pipes/abs.pipe';
import { AssetApprovalLimitService } from '../../../services/asset-approval-limit.service';
import { AssetService } from '../../../services/asset.service';
import { DialogService } from '../../../services/dialog.service';
import { CircleProgressBarComponent } from '../../../shared/components/circle-progress-bar/circle-progress-bar.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ApprovalDoneDialogComponent } from '../../dialogs/approval-done-dialog/approval-done-dialog.component';

@Component({
  selector: 'app-approval-limit-info',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './approval-limit-info.component.html',
  styleUrls: ['./approval-limit-info.component.scss'],
  animations: [trigger('approvalLimitInfo', moveRightIf)],
  imports: [CircleProgressBarComponent, AbsPipe, SvgComponent, AsyncPipe],
})
export class ApprovalLimitInfoComponent implements OnChanges, OnDestroy {
  private assetApprovalLimitService = inject(AssetApprovalLimitService);
  private assetService = inject(AssetService);
  private dialog = inject(DialogService);
  private check = inject(ChangeDetectorRef);
  private destroyed$ = new Subject<void>();
  @Input({ required: true }) gallery: GalleryDto;
  showInfo = false;
  progress = 0;
  public approvedAssetCount$: Observable<number>;
  private statusChange$ = this.assetService.updates
    .pipe(
      filter(event => {
        if (event.asset?.gallery !== this.gallery.id) return;
        if (!event.isOptimistic) return;
        const { asset, previousValue } = event;
        if (!previousValue) return;
        return (asset.status === 'approved') !== (previousValue.status === 'approved');
      }),
      map(event => event.asset.status),
      takeUntil(this.destroyed$)
    )
    .subscribe(() => {
      this.showInfo = true;
      this.updateProgress(true);
      this.hideInfo();
      this.check.detectChanges();
    });

  ngOnChanges(): void {
    if (this.gallery) {
      this.approvedAssetCount$ = this.assetApprovalLimitService.getApprovedAssetCountForGallery(this.gallery.id);
    }
    this.updateProgress();
  }

  hideInfo = debounce(() => {
    this.showInfo = false;
    this.check.detectChanges();
  }, 1000 * 2);

  updateProgress(confirmWhenReachingLimit = false) {
    this.approvedAssetCount$.pipe(take(1), delay(100)).subscribe(count => {
      const previousValue = this.progress;
      this.progress = this.calcProgress(count);
      this.check.detectChanges();
      if (confirmWhenReachingLimit && this.progress === 100 && previousValue < 100) {
        this.dialog.open(ApprovalDoneDialogComponent, { gallery: this.gallery });
      }
    });
  }

  private calcProgress(count: number) {
    return clamp(count / Math.abs(this.gallery.approval_limits), 0, 1) * 100;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
